import React from 'react';
import { useTranslation } from 'react-i18next';
import type { FAQComponent } from '../../../types/builder';
import { useBuilderStore } from '../../../store/builderStore';
import { ColorPicker } from '../../Common/ColorPicker';
import { Plus, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';

interface FAQSettingsProps {
  component: FAQComponent;
}

const STYLE_OPTIONS = ['default', 'modern', 'minimal', 'card'] as const;

export const FAQSettings: React.FC<FAQSettingsProps> = ({ component }) => {
  const { t } = useTranslation('builder');
  const { updateComponent } = useBuilderStore();

  const handleUpdate = (updates: Partial<FAQComponent['settings']>) => {
    updateComponent(component.id, {
      settings: { ...component.settings, ...updates },
    });
  };

  const addItem = () => {
    const newItem = {
      id: `item-${Date.now()}`,
      question: t('settings.faq.newQuestion'),
      answer: t('settings.faq.newAnswer'),
    };
    handleUpdate({ items: [...component.settings.items, newItem] });
  };

  const removeItem = (itemId: string) => {
    if (component.settings.items.length <= 1) {
      toast.error(t('settings.faq.atLeastOneItem'));
      return;
    }
    handleUpdate({
      items: component.settings.items.filter((item) => item.id !== itemId),
    });
  };

  const updateItem = (itemId: string, updates: any) => {
    handleUpdate({
      items: component.settings.items.map((item) =>
        item.id === itemId ? { ...item, ...updates } : item
      ),
    });
  };

  return (
    <div className="space-y-6">
      {/* Style Selection */}
      <div>
        <label className="block text-sm font-medium mb-2">{t('settings.faq.styleLabel')}</label>
        <select
          value={component.settings.style || 'default'}
          onChange={(e) => handleUpdate({ style: e.target.value as any })}
          className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          {STYLE_OPTIONS.map((key) => (
            <option key={key} value={key}>
              {t(`settings.common.styles.${key}` as const)}
            </option>
          ))}
        </select>
      </div>

      {/* Title */}
      <div>
        <label className="block text-sm font-medium mb-2">{t('settings.faq.title')}</label>
        <input
          type="text"
          value={component.settings.title}
          onChange={(e) => handleUpdate({ title: e.target.value })}
          className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder={t('settings.faq.titlePlaceholder')}
        />
      </div>

      {/* FAQ Items */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <label className="block text-sm font-medium">{t('settings.faq.items')}</label>
          <button
            onClick={addItem}
            className="flex items-center gap-1 px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700"
          >
            <Plus size={16} />
            {t('settings.faq.addItem')}
          </button>
        </div>

        <div className="space-y-4 max-h-96 overflow-y-auto">
          {component.settings.items.map((item, index) => (
            <div
              key={item.id}
              className="border border-gray-300 rounded-lg p-3 bg-gray-50"
            >
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-700">
                  {t('settings.faq.itemNumber', { n: index + 1 })}
                </span>
                {component.settings.items.length > 1 && (
                  <button
                    onClick={() => removeItem(item.id)}
                    className="text-red-600 hover:text-red-700"
                  >
                    <Trash2 size={16} />
                  </button>
                )}
              </div>

              {/* Question */}
              <div className="mb-3">
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  {t('settings.faq.question')}
                </label>
                <input
                  type="text"
                  value={item.question}
                  onChange={(e) => updateItem(item.id, { question: e.target.value })}
                  className="w-full px-2 py-1 bg-white border border-gray-300 rounded text-sm"
                  placeholder={t('settings.faq.questionPlaceholder')}
                />
              </div>

              {/* Answer */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  {t('settings.faq.answer')}
                </label>
                <textarea
                  value={item.answer}
                  onChange={(e) => updateItem(item.id, { answer: e.target.value })}
                  rows={3}
                  className="w-full px-2 py-1 bg-white border border-gray-300 rounded text-sm"
                  placeholder={t('settings.faq.answerPlaceholder')}
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Text Color */}
      <div>
        <label className="block text-sm font-medium mb-2">{t('settings.common.textColor')}</label>
        <ColorPicker
          color={component.settings.textColor}
          onChange={(color) => handleUpdate({ textColor: color })}
        />
      </div>

      {/* Background Color */}
      <div>
        <label className="block text-sm font-medium mb-2">{t('settings.common.backgroundColor')}</label>
        <ColorPicker
          color={component.settings.backgroundColor}
          onChange={(color) => handleUpdate({ backgroundColor: color })}
        />
      </div>

      {/* Padding */}
      <div>
        <label className="block text-sm font-medium mb-2">
          {t('settings.common.paddingPx', { value: component.settings.padding })}
        </label>
        <input
          type="range"
          min="0"
          max="100"
          value={component.settings.padding}
          onChange={(e) => handleUpdate({ padding: parseInt(e.target.value) })}
          className="w-full"
        />
      </div>
    </div>
  );
};
